import {captureMessage} from '@sentry/browser';

import {isEmpty, isNil} from 'ramda';

import {RequestCancelledError} from '../api/requestCancelledError';
import type {useHttpCalls} from '../api/useHttpCalls';
import type {
  GetDataQueryResponse,
  GetPresetResponse,
  Preset,
  PutPresetRequest,
  QueryIdRequestBody,
} from '../types/Api';
import type {QueryFilterObject} from '../types/QueryFilterObject';
import {objectHasEveryEqualPropOfAnother} from './objectHasEveryEqualPropOfAnother';

type HttpCalls = ReturnType<typeof useHttpCalls>;

/**
 * Result of reconciling a single preset against the caller's static filters.
 *
 * Always returns the fetched `dataQuery` so callers can avoid re-fetching it (e.g. EagGrid
 * Phase 2 for the active preset). Returns `detail` only when reconciliation actually had to
 * mint a new data query — in that case the detail was needed for the PUT and is reused.
 */
export type ReconcileResult = {
  dataQuery: GetDataQueryResponse;
  detail?: GetPresetResponse;
  /** New dataQueryId minted during reconciliation, if filters had to be updated. */
  newDataQueryId?: string;
};

/**
 * Reconciles a single preset's stored data query against the caller-supplied
 * `staticFilters`, repointing the preset at a fresh data query when they diverge.
 *
 * Steps:
 *   1. `GET /data-query/:id` for the preset's stored query.
 *   2. If every entry in `staticFilters` is already present and equal, return early.
 *   3. Otherwise run `POST /data-query` (mint new query) and `GET /preset/:id` (fetch full
 *      detail) in parallel — they are independent.
 *   4. `PUT /preset/:id` repointing the preset at the new `dataQueryId`. If the preset detail
 *      is missing/empty, a Sentry message is captured and the update is skipped.
 *
 * Best-effort: the final `updatePreset` PUT swallows errors so a transient 5xx does not
 * break preset loading; the next page load will retry.
 *
 * Returns `undefined` if the signal is aborted before the stored query was fetched;
 * otherwise returns the fetched data query, and the detail + new query id when
 * reconciliation minted a new query. Callers can reuse these to avoid duplicate
 * fetches downstream.
 *
 * @param signal - Optional AbortSignal. When aborted, remaining side-effects
 *   (POST /data-query, PUT /preset) are skipped and the function returns early.
 *   Requests already in flight when the signal fires are not retracted, but their
 *   results are discarded.
 */
export const reconcilePresetWithQueryModifier = async (
  preset: Preset,
  http: HttpCalls,
  staticFilters: QueryFilterObject,
  signal?: AbortSignal,
  options?: {updateSourcePreset?: boolean}
): Promise<ReconcileResult | undefined> => {
  if (signal?.aborted) {
    return;
  }

  const data =
    options?.updateSourcePreset === false
      ? await http.getDataQuery(preset.dataQueryId, {forCopy: true})
      : await http.getDataQuery(preset.dataQueryId);

  if (signal?.aborted || !data || isNil(data.filters)) {
    return;
  }

  if (objectHasEveryEqualPropOfAnother(staticFilters, data.filters)) {
    return {dataQuery: data};
  }

  // POST /data-query and GET /preset/:id are independent — run them in parallel.
  let queryIdResponseBody: Awaited<ReturnType<HttpCalls['createDataQuery']>>;
  let presetData: Awaited<ReturnType<HttpCalls['getPreset']>>;
  try {
    const queryBody: QueryIdRequestBody = {
      filters: {...data.filters, ...staticFilters} as QueryIdRequestBody['filters'],
      order: data.order,
      // Empty string means "no smart search" — normalize to null so grids with
      // smart search disabled don't get a query minted with smartSearch: "".
      smartSearch: data.smartSearch || null,
    };
    [queryIdResponseBody, presetData] = await Promise.all([
      options?.updateSourcePreset === false
        ? http.createDataQuery(queryBody, signal, {forCopy: true})
        : http.createDataQuery(queryBody, signal),
      options?.updateSourcePreset === false
        ? Promise.resolve(undefined)
        : http.getPreset(preset.id),
    ]);
  } catch (error) {
    // An abort mid-POST rejects with RequestCancelledError (see handleApiError) —
    // same outcome as the post-settle aborted branch below: skip the side-effects
    // and hand back the stored query.
    if (error instanceof RequestCancelledError) {
      return {dataQuery: data};
    }
    throw error;
  }

  if (signal?.aborted) {
    return {dataQuery: data};
  }

  if (!queryIdResponseBody) {
    return {dataQuery: data, detail: presetData ?? undefined};
  }

  // Copying a saved preset into Default needs its filters reconciled, but must
  // leave the saved source untouched (including any just-saved grid settings).
  if (options?.updateSourcePreset === false) {
    return {dataQuery: data, newDataQueryId: queryIdResponseBody.dataQueryId};
  }

  if (
    !presetData ||
    isNil(presetData.gridSettings) ||
    isEmpty(presetData.gridSettings) ||
    isNil(presetData.columnsSettings) ||
    isEmpty(presetData.columnsSettings)
  ) {
    captureMessage('updatePreset', (scope) =>
      scope.setLevel('error').setExtras({
        userDescription: 'update custom preset from reconcilePresetWithQueryModifier',
        state: presetData,
      })
    );
    return {dataQuery: data, newDataQueryId: queryIdResponseBody.dataQueryId};
  }

  const modifiedPreset: PutPresetRequest = {
    gridSettings: presetData.gridSettings,
    columnsSettings: presetData.columnsSettings,
    dataQueryId: queryIdResponseBody.dataQueryId,
  };

  // Silent catch — queryModifier reconciliation is best-effort. Skipped entirely
  // when the caller has unmounted (aborted signal): a PUT after unmount would
  // race the next mount's reconciliation.
  if (!signal?.aborted) {
    http.updatePreset(preset.id, modifiedPreset).catch(() => {});
  }

  return {
    dataQuery: data,
    detail: {...presetData, dataQueryId: queryIdResponseBody.dataQueryId},
    newDataQueryId: queryIdResponseBody.dataQueryId,
  };
};
